import { get, ref } from 'firebase/database';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { database } from '@skippay/core/firebase/firebase';
import {
	BreadcrumbCategory,
	captureBreadcrumb,
	captureException,
} from '@skippay/core/global/errorTracking';
import {
	getUserFirebaseUid,
	setBanners,
	setConfig,
	setModals,
	setRequests,
} from '@skippay/core/modules/user';

function getDatabaseRef(user: string, collection: string) {
	return ref(database, `users/${user}/${collection}`);
}

interface Props {
	isLoggedIn: boolean;
}

export function useFirebaseSync({ isLoggedIn }: Props): void {
	const dispatch = useDispatch();
	const userUid = useSelector(getUserFirebaseUid);

	useEffect(() => {
		if (!userUid || !isLoggedIn) {
			return;
		}

		captureBreadcrumb(BreadcrumbCategory.firebaseDatabase, 'Downloading latest data from Firebase');

		try {
			const bannersPromise = get(getDatabaseRef(userUid, 'banners'));
			const modalsPromise = get(getDatabaseRef(userUid, 'modals'));
			const requestsPromise = get(getDatabaseRef(userUid, 'requests'));
			const configPromise = get(getDatabaseRef(userUid, 'config'));

			Promise.all([bannersPromise, modalsPromise, requestsPromise, configPromise])
				.then(([bannersSnapshot, modalsSnapshot, requestsSnapshot, configSnapshot]) => {
					if (bannersSnapshot.exists()) {
						const banners = bannersSnapshot.val();
						dispatch(setBanners(banners));
					} else {
						dispatch(setBanners({}));
					}

					if (modalsSnapshot.exists()) {
						const modals = modalsSnapshot.val();
						dispatch(setModals(modals));
					} else {
						dispatch(setModals({}));
					}

					if (requestsSnapshot.exists()) {
						const requests = requestsSnapshot.val();
						dispatch(setRequests(requests));
					} else {
						dispatch(setRequests({}));
					}

					if (configSnapshot.exists()) {
						const config = configSnapshot.val();
						dispatch(setConfig(config));
					}
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error(error);
					captureException(new Error('Error while downloading latest data from Firebase'), {
						extra: { error },
					});
				});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			captureException(new Error('Error while getting latest data from Firebase'), {
				extra: {
					error,
				},
			});
		}
	}, [dispatch, userUid, isLoggedIn]);
}
